import * as React from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useCreateBlockRule } from '@/lib/api/blockRules';
import { BlockRuleForm, BlockRuleFormValues } from '../components/block-rule-form';
import { toMutationInput } from '../utils';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(CreateBlockRulePage, PERMISSIONS.PROCESSORS_MANAGE);

function CreateBlockRulePage() {
    const { orgRoutes, navigate } = useOrgNavigate();
    const { mutateAsync: createBlockRule, isPending } = useCreateBlockRule();

    const handleSubmit = React.useCallback(
        async (values: BlockRuleFormValues) => {
            const input = toMutationInput(values);
            await createBlockRule(input);
            navigate(orgRoutes.BLOCK_RULES());
        },
        [createBlockRule, navigate, orgRoutes],
    );

    const handleCancel = React.useCallback(() => {
        navigate(orgRoutes.BLOCK_RULES());
    }, [navigate, orgRoutes]);

    return (
        <div className="flex flex-col space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={handleCancel}>
                        <ArrowLeft className="size-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Add block rule</h1>
                        <p className="text-muted-foreground text-sm">
                            Define conditions to block payments matching specific attributes.
                        </p>
                    </div>
                </div>
                <div className="flex flex-row gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" size="sm" form="block-rule-form" disabled={isPending}>
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create
                    </Button>
                </div>
            </div>
            <Card>
                <CardContent className="pt-6">
                    <BlockRuleForm
                        onSubmit={handleSubmit}
                        isSubmitting={isPending}
                    />
                </CardContent>
            </Card>
        </div>
    );
}
